import type { Program } from 'models';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIcon, Col, EventIcon, Modal, PollIcon, PostIcon, Row, Text, WithingsOrderPostIcon } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { CreateProgramEvent } from 'features/events/CreateProgramEvent';
import { CreatePoll } from 'features/polls';
import { CreateActivity } from '../../feed';
import { CreatePost } from '../../posts';
import { CreateWithingsOrderPost } from '../../subscribe';
import { CreateContentButton } from './create-content-button';

type ContentType = 'activity' | 'event' | 'order' | 'poll' | 'post' | null;

type P = {
  isOpened: boolean;
  onCancel: () => void;
  programContentObject: Program;
};

const CreateContentModal = ({ isOpened, onCancel, programContentObject }: P) => {
  const { t } = useTranslation();
  const [contentType, setContentType] = useState<ContentType>(null);
  const [modal, contextHolder] = Modal.useModal();

  const { topics, withingsDevices } = programContentObject;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- We cannot tell with certainty that topics is array with lenght
  const preSelectedProgramTopic = topics?.length === 1 ? topics[0] : undefined;

  const close = () => {
    setContentType(null);
    onCancel();
  };

  const confirmDiscard = (content: string) => {
    modal.confirm({
      content,
      okText: t('Discard'),
      onOk: close,
      title: t('Discard content'),
    });
  };

  const onCancelEvent = (force?: boolean) => {
    if (force === true) {
      close();
      return;
    }

    confirmDiscard(t('Do you wish to discard your Event?'));
  };

  const onCancelPost = () => {
    confirmDiscard(t('Do you wish to discard your Post?'));
  };

  const onCancelPoll = () => {
    confirmDiscard(t('Do you wish to discard your Poll?'));
  };

  const onCancelActivity = () => {
    confirmDiscard(t('Do you wish to discard your Activity?'));
  };

  const onCancelCreateOrder = () => {
    confirmDiscard(t('Do you wish to discard your Order?'));
  };

  const onCreateEvent = () => {
    onCancelEvent(true);
  };

  // eslint-disable-next-line @typescript-eslint/require-await -- Signature must match Create*'s onSuccess: () => Promise<void>
  const onCreateActivity = async () => {
    close();
  };

  const onCreatePoll = () => {
    close();
  };

  // eslint-disable-next-line @typescript-eslint/require-await -- Signature must match Create*'s onSuccess: () => Promise<void>
  const onCreatePost = async () => {
    close();
  };

  const onCreateOrder = () => {
    close();
  };

  const onRequestReturn = () => {
    setContentType(null);
  };

  return (
    <>
      {contextHolder}

      <Modal
        isOpened={isOpened ? contentType === null : false}
        onCancel={onCancel}
        title="Create content"
        width="medium"
      >
        <Modal.HugContent>
          <Row gutter={['ms', 'ms']}>
            <Col span={24}>
              <Text size="large" type="title" weight="bold">
                What type of content do you want to create?
              </Text>
            </Col>
            <Col span={12}>
              <CreateContentButton
                icon={<PostIcon size="middle" type="primary" />}
                onClick={() => {
                  setContentType('post');
                }}
                title="Create Post"
              />
            </Col>
            <Col span={12}>
              <CreateContentButton
                icon={<EventIcon size="middle" type="primary" />}
                onClick={() => {
                  setContentType('event');
                }}
                title="Create Event"
              />
            </Col>
            <Col span={12}>
              <CreateContentButton
                icon={<PollIcon size="middle" type="primary" />}
                onClick={() => {
                  setContentType('poll');
                }}
                title="Create Poll"
              />
            </Col>
            <Col span={12}>
              <CreateContentButton
                icon={<ActivityIcon size="middle" type="primary" />}
                onClick={() => {
                  setContentType('activity');
                }}
                title="Create Activity"
              />
            </Col>
            {withingsDevices?.length ? (
              <Col span={12}>
                <CreateContentButton
                  icon={<WithingsOrderPostIcon size="middle" type="primary" />}
                  onClick={() => {
                    setContentType('order');
                  }}
                  title="Create Withings order"
                />
              </Col>
            ) : null}
          </Row>
        </Modal.HugContent>
      </Modal>

      <CreateProgramEvent
        isOpened={contentType === 'event'}
        onCancel={() => {
          onCancelEvent(false);
        }}
        onRequestReturn={onRequestReturn}
        onSuccess={onCreateEvent}
        preSelectedProgramTopic={preSelectedProgramTopic}
        program={programContentObject}
      />

      <CreatePoll
        isOpened={contentType === 'poll'}
        onCancel={onCancelPoll}
        onSuccess={onCreatePoll}
        preSelectedProgramTopic={preSelectedProgramTopic}
        program={programContentObject}
      />

      <CreatePost
        isOpened={contentType === 'post'}
        onCancel={onCancelPost}
        onSuccess={onCreatePost}
        preSelectedProgramTopic={preSelectedProgramTopic}
        program={programContentObject}
      />

      <CreateActivity
        isOpened={contentType === 'activity'}
        onCancel={onCancelActivity}
        onRequestReturn={onRequestReturn}
        onSuccess={onCreateActivity}
        preSelectedProgramTopic={preSelectedProgramTopic}
        program={programContentObject}
      />

      <CreateWithingsOrderPost
        isOpened={contentType === 'order'}
        onCancel={onCancelCreateOrder}
        onRequestReturn={onRequestReturn}
        onSuccess={onCreateOrder}
        preSelectedProgramTopic={preSelectedProgramTopic}
        program={programContentObject}
      />
    </>
  );
};

export default CreateContentModal;
